/**
 * Transfer Portal — SLAM Fit Score
 * Powered by DraftExpress Gold data + Gemini 3 Pro
 *
 * Left panel: player list with position/class/status/search filters
 * Right panel: player stats grid + SLAM Fit Score card + breakdown modal
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { C } from '../../components/design-system/primitives';
import { useTeam } from '../../lib/teamConfig';
import useMobileDetect from '../../hooks/useMobileDetect';
import { getAuthToken } from '../../utils/helpers';
import config from '../../utils/config';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3,
  Shield,
  Brain,
  Zap,
  ChevronRight,
  X,
  ArrowLeft,
  Loader2,
  ExternalLink,
  Target,
  Activity,
  Crosshair,
  Globe,
  Search,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

// ═══════════════════════════════════════════════════════════════════════════
// LOCAL CONSTANTS — do NOT use global tier() — thresholds differ
// ═══════════════════════════════════════════════════════════════════════════

const portalTier = (score) => {
  if (score >= 85) return { label: 'Elite Fit', color: C.gen };
  if (score >= 70) return { label: 'Strong Fit', color: C.elite };
  if (score >= 55) return { label: 'Solid Fit', color: C.gold };
  if (score >= 40) return { label: 'Developmental Fit', color: C.goldMuted };
  return { label: 'Poor Fit', color: C.dev };
};

const DIMENSION_CONFIG = [
  { key: 'statisticalScore', label: 'Statistical', icon: BarChart3, color: '#00AAFF' },
  { key: 'characterScore',   label: 'Character',   icon: Shield,    color: '#00CC66' },
  { key: 'cognitiveScore',   label: 'Cognitive',   icon: Brain,     color: '#E8B800' },
  { key: 'slamCompositeScore', label: 'SLAM Composite', icon: Zap,  color: '#D4A017' },
];

const POSITIONS = ['ALL', 'PG', 'SG', 'SF', 'PF', 'C'];
const CLASS_YEARS = ['ALL', 'Freshman', 'Sophomore', 'Junior', 'Senior'];
const PORTAL_STATUSES = ['ALL', 'Available', 'Committed'];
const SORT_OPTIONS = [
  { value: 'dxGrade', label: 'DX Grade' },
  { value: 'ppg', label: 'PPG' },
];

const API_BASE = config.SERVER_BASE_URL;

// ═══════════════════════════════════════════════════════════════════════════
// HELPER SUB-COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════

function FilterPill({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '4px 10px',
        borderRadius: 100,
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: 1,
        cursor: 'pointer',
        border: active ? `1px solid ${C.gold}44` : `1px solid ${C.border}`,
        background: active ? C.goldDim : 'transparent',
        color: active ? C.gold : C.t2,
        transition: 'all 0.15s',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </button>
  );
}

function ScoreBadge({ score, small }) {
  if (!score && score !== 0) return null;
  const t = portalTier(score);
  return (
    <span style={{
      fontSize: small ? 9 : 10,
      fontWeight: 700,
      fontFamily: 'monospace',
      color: t.color,
      background: `${t.color}18`,
      border: `1px solid ${t.color}33`,
      borderRadius: 4,
      padding: small ? '1px 4px' : '2px 6px',
    }}>
      {score}
    </span>
  );
}

function DataSourceBadge({ source }) {
  const isLive = source === 'live_data';
  return (
    <span style={{
      fontSize: 9,
      fontWeight: 600,
      letterSpacing: 2,
      padding: '2px 6px',
      borderRadius: 4,
      textTransform: 'uppercase',
      ...(isLive
        ? { background: C.goldDim, color: C.gold }
        : { background: C.s3, color: C.t2, border: `1px solid ${C.border}` }
      ),
    }}>
      {isLive ? 'FILM DATA' : 'STATS-BASED'}
    </span>
  );
}

function ScoutingNotesBadge() {
  return (
    <span style={{
      fontSize: 9,
      fontWeight: 600,
      letterSpacing: 2,
      padding: '2px 6px',
      borderRadius: 4,
      textTransform: 'uppercase',
      background: C.s3,
      color: C.t2,
      border: `1px solid ${C.border}`,
    }}>
      SCOUTING NOTES
    </span>
  );
}

function ProgressBar({ value, color, max = 100 }) {
  const pct = Math.min(100, Math.max(0, (value / max) * 100));
  return (
    <div style={{
      background: C.s3,
      borderRadius: 3,
      overflow: 'hidden',
      height: 6,
      flex: 1,
    }}>
      <div style={{
        width: `${pct}%`,
        height: '100%',
        background: color,
        borderRadius: 3,
        transition: 'width 0.6s ease',
      }} />
    </div>
  );
}

function StatCell({ label, value }) {
  const display = (value === 0 || value === null || value === undefined) ? '--' : value;
  return (
    <div style={{
      background: C.s2,
      border: `1px solid ${C.border}`,
      borderRadius: 8,
      padding: '8px 10px',
      textAlign: 'center',
    }}>
      <div style={{ fontSize: 9, color: C.t3, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 17, fontWeight: 300, color: C.text, fontFamily: 'monospace' }}>
        {display}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// FIT SCORE BREAKDOWN MODAL
// 4 tabs: Statistical | Character | Cognitive | Program Fit
// ═══════════════════════════════════════════════════════════════════════════

function FitScoreBreakdownModal({ player, fitScore, onClose }) {
  const [activeTab, setActiveTab] = useState('statistical');

  const tabs = [
    { id: 'statistical', label: 'Statistical' },
    { id: 'character',   label: 'Character' },
    { id: 'cognitive',   label: 'Cognitive' },
    { id: 'program',     label: 'Program Fit' },
  ];

  const cs3 = fitScore.cs3SubScores || {};

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.92)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '16px',
    }}>
      <div
        className="animate-fadeIn"
        style={{
          background: C.s1,
          border: `1px solid ${C.border}`,
          borderRadius: 20,
          width: '100%',
          maxWidth: 680,
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Modal header */}
        <div style={{
          padding: '20px 24px 0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
        }}>
          <div>
            <div style={{ fontSize: 9, color: C.t3, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 4 }}>
              SLAM Fit Score — Full Breakdown
            </div>
            <div style={{ fontSize: 15, color: C.text, fontWeight: 300 }}>
              {player.playerName} <span style={{ color: C.t3, fontSize: 12 }}>• {player.school}</span>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: C.t2, padding: 4 }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Tab bar */}
        <div style={{
          display: 'flex', gap: 4, padding: '16px 24px 0', flexShrink: 0,
        }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '6px 14px',
                borderRadius: 100,
                fontSize: 11,
                fontWeight: 500,
                cursor: 'pointer',
                border: 'none',
                background: activeTab === tab.id ? C.gold : 'transparent',
                color: activeTab === tab.id ? '#000' : C.t2,
                transition: 'all 0.15s',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div style={{ overflowY: 'auto', padding: '20px 24px 24px', flex: 1 }}>
          {/* STATISTICAL TAB */}
          {activeTab === 'statistical' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ fontSize: 10, color: C.t3, letterSpacing: 2, textTransform: 'uppercase' }}>Statistical Profile</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                <StatCell label="PPG" value={player.stats.ppg} />
                <StatCell label="RPG" value={player.stats.rpg} />
                <StatCell label="APG" value={player.stats.apg} />
                <StatCell label="FG%" value={player.stats.fgPct ? `${player.stats.fgPct}%` : '--'} />
                <StatCell label="3P%" value={player.stats.fg3Pct ? `${player.stats.fg3Pct}%` : '--'} />
                <StatCell label="FT%" value={player.stats.ftPct ? `${player.stats.ftPct}%` : '--'} />
                <StatCell label="PER" value={player.stats.per} />
                <StatCell label="P40" value={player.stats.p40} />
                <StatCell label="GP" value={player.stats.gamesPlayed} />
              </div>
              {fitScore.statisticalHighlights && fitScore.statisticalHighlights.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                  {fitScore.statisticalHighlights.map((h, i) => (
                    <span key={i} style={{
                      background: '#00AAFF18', border: '1px solid #00AAFF33',
                      borderRadius: 6, padding: '4px 10px', fontSize: 11, color: C.gen,
                    }}>
                      {h}
                    </span>
                  ))}
                </div>
              )}
              {fitScore.statisticalNarrative && (
                <p style={{ fontSize: 12, color: C.t2, lineHeight: 1.7, margin: 0 }}>
                  {fitScore.statisticalNarrative}
                </p>
              )}
            </div>
          )}

          {/* CHARACTER TAB */}
          {activeTab === 'character' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ fontSize: 10, color: C.t3, letterSpacing: 2, textTransform: 'uppercase' }}>Character Assessment</div>
                <ScoutingNotesBadge />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ fontSize: 36, fontWeight: 200, color: '#00CC66', fontFamily: 'monospace' }}>
                  {fitScore.characterScore}
                </div>
                <ProgressBar value={fitScore.characterScore} color="#00CC66" />
              </div>
              {fitScore.characterSignals && fitScore.characterSignals.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {fitScore.characterSignals.map((s, i) => (
                    <span key={i} style={{
                      background: '#00CC6618', border: '1px solid #00CC6633',
                      borderRadius: 6, padding: '4px 10px', fontSize: 11, color: C.elite,
                    }}>
                      {s}
                    </span>
                  ))}
                </div>
              )}
              {fitScore.characterNarrative && (
                <p style={{ fontSize: 12, color: C.t2, lineHeight: 1.7, margin: 0 }}>
                  {fitScore.characterNarrative}
                </p>
              )}
              <div style={{
                background: C.s2, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 14px',
              }}>
                <p style={{ fontSize: 11, color: C.t3, margin: 0, lineHeight: 1.6 }}>
                  Character score is derived from DraftExpress scouting narrative signals. Work ethic, coachability,
                  and team culture fit are inferred from the narrative — not from verified survey data.
                </p>
              </div>
            </div>
          )}

          {/* COGNITIVE TAB */}
          {activeTab === 'cognitive' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ fontSize: 10, color: C.t3, letterSpacing: 2, textTransform: 'uppercase' }}>Cognitive Assessment (CS3)</div>
                <DataSourceBadge source={fitScore.cognitiveDataSource} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ fontSize: 36, fontWeight: 200, color: '#E8B800', fontFamily: 'monospace' }}>
                  {fitScore.cognitiveScore}
                </div>
                <ProgressBar value={fitScore.cognitiveScore} color="#E8B800" />
              </div>
              {/* CS3 Sub-dimensions */}
              {cs3 && Object.keys(cs3).length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {[
                    { label: 'CS3 Score', value: cs3.cs3Score, isNum: true },
                    { label: 'Decision Quality', value: cs3.decisionQuality },
                    { label: 'Cognitive Load', value: cs3.cognitiveLoad },
                    { label: 'Dominant State', value: cs3.dominantState },
                    { label: 'Clutch Indicators', value: cs3.clutchIndicators },
                  ].map((row, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ fontSize: 10, color: C.t3, minWidth: 120 }}>{row.label}</div>
                      {row.isNum && row.value !== null && row.value !== undefined ? (
                        <ProgressBar value={row.value} color="#E8B800" />
                      ) : (
                        <span style={{ fontSize: 11, color: C.t2, fontFamily: 'monospace' }}>
                          {row.value || '--'}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {fitScore.cognitiveSignals && fitScore.cognitiveSignals.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {fitScore.cognitiveSignals.map((s, i) => (
                    <span key={i} style={{
                      background: `${C.goldDim}`, border: `1px solid ${C.gold}33`,
                      borderRadius: 6, padding: '4px 10px', fontSize: 11, color: C.gold,
                    }}>
                      {s}
                    </span>
                  ))}
                </div>
              )}
              {fitScore.cognitiveNarrative && (
                <p style={{ fontSize: 12, color: C.t2, lineHeight: 1.7, margin: 0 }}>
                  {fitScore.cognitiveNarrative}
                </p>
              )}
            </div>
          )}

          {/* PROGRAM FIT TAB */}
          {activeTab === 'program' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ fontSize: 10, color: C.t3, letterSpacing: 2, textTransform: 'uppercase' }}>Program Fit Analysis</div>
              {/* SLAM Composite score */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ fontSize: 36, fontWeight: 200, color: '#D4A017', fontFamily: 'monospace' }}>
                  {fitScore.slamCompositeScore}
                </div>
                <ProgressBar value={fitScore.slamCompositeScore} color="#D4A017" />
              </div>
              {/* Program fit factors */}
              {fitScore.programFitFactors && fitScore.programFitFactors.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {fitScore.programFitFactors.map((f, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                      <div style={{ width: 4, height: 4, borderRadius: '50%', background: C.gold, marginTop: 6, flexShrink: 0 }} />
                      <span style={{ fontSize: 12, color: C.t2 }}>{f}</span>
                    </div>
                  ))}
                </div>
              )}
              {/* NIL estimate */}
              {fitScore.nilEstimate > 0 && (
                <div style={{ background: C.s2, border: `1px solid ${C.border}`, borderRadius: 10, padding: '12px 16px' }}>
                  <div style={{ fontSize: 9, color: C.t3, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 6 }}>NIL Estimate</div>
                  <div style={{ fontSize: 22, fontWeight: 200, color: C.gold, fontFamily: 'monospace', marginBottom: 4 }}>
                    Est. ~${(fitScore.nilEstimate / 1000).toFixed(0)}K/yr
                  </div>
                  <p style={{ fontSize: 10, color: C.t3, margin: 0 }}>
                    AI estimate — not a verified marketplace figure
                  </p>
                </div>
              )}
              {/* Key recruiting insight */}
              {fitScore.keyRecruitingInsight && (
                <div style={{
                  borderLeft: `3px solid ${C.gold}`,
                  background: C.s2,
                  borderRadius: '0 8px 8px 0',
                  padding: '12px 16px',
                }}>
                  <div style={{ fontSize: 9, color: C.gold, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 6 }}>
                    Key Recruiting Insight
                  </div>
                  <p style={{ fontSize: 12, color: C.text, lineHeight: 1.7, margin: 0 }}>
                    {fitScore.keyRecruitingInsight}
                  </p>
                </div>
              )}
              {/* Red flags */}
              {fitScore.redFlags && fitScore.redFlags.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ fontSize: 9, color: C.dev, letterSpacing: 2, textTransform: 'uppercase' }}>Red Flags</div>
                  {fitScore.redFlags.map((f, i) => (
                    <div key={i} style={{ fontSize: 12, color: C.dev, paddingLeft: 12 }}>• {f}</div>
                  ))}
                </div>
              )}
              {fitScore.slamCompositeNarrative && (
                <p style={{ fontSize: 12, color: C.t2, lineHeight: 1.7, margin: 0 }}>
                  {fitScore.slamCompositeNarrative}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// DEEP RESEARCH MODAL — 8-Lens Scout Report
// ═══════════════════════════════════════════════════════════════════════════

const LENS_TABS = [
  { id: 'offensive', label: 'Offensive', icon: Target, color: '#FF6B00' },
  { id: 'defensive', label: 'Defensive', icon: Shield, color: '#00D4FF' },
  { id: 'player_performance', label: 'Performance', icon: Activity, color: '#00FF88' },
  { id: 'tactical', label: 'Tactical', icon: Crosshair, color: '#E8B800' },
  { id: 'biomechanics', label: 'Biomechanics', icon: Zap, color: '#FF44CC' },
  { id: 'shooting', label: 'Shooting', icon: Target, color: '#8B5CF6' },
  { id: 'cognitive', label: 'Cognitive', icon: Brain, color: '#06B6D4' },
  { id: 'external_intelligence', label: 'Intel', icon: Globe, color: '#F97316' },
];

function DeepResearchGradeCircle({ grade, color, size = 72 }) {
  const radius = (size / 2) - 6;
  const circumference = 2 * Math.PI * radius;
  const pct = grade != null ? (grade / 10) : 0;
  const dashLen = pct * circumference;
  const gid = `dr-grade-${Math.random().toString(36).slice(2, 8)}`;

  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={4}
        />
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke={`url(#${gid})`} strokeWidth={4}
          strokeLinecap="round"
          strokeDasharray={`${dashLen} ${circumference}`}
          style={{ transition: 'stroke-dasharray 0.8s ease' }}
        />
        <defs>
          <linearGradient id={gid} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={color} />
            <stop offset="100%" stopColor={`${color}88`} />
          </linearGradient>
        </defs>
      </svg>
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <span style={{ fontSize: size * 0.32, fontWeight: 700, color: '#F5F5F5', fontFamily: 'monospace' }}>
          {grade != null ? grade.toFixed(1) : '--'}
        </span>
      </div>
    </div>
  );
}

function ConfidenceBadge({ level }) {
  const config = {
    high: { color: '#00CC66', bg: '#00CC6618', border: '#00CC6633', label: 'HIGH' },
    medium: { color: '#E8B800', bg: '#E8B80018', border: '#E8B80033', label: 'MED' },
    low: { color: '#FF4444', bg: '#FF444418', border: '#FF444433', label: 'LOW' },
  };
  const c = config[level] || config.low;
  return (
    <span style={{
      fontSize: 9, fontWeight: 700, letterSpacing: 2,
      padding: '2px 8px', borderRadius: 4,
      background: c.bg, border: `1px solid ${c.border}`, color: c.color,
    }}>
      {c.label} CONFIDENCE
    </span>
  );
}

function DeepResearchModal({ player, researchData, researchLoading, onClose }) {
  const [activeTab, setActiveTab] = useState('offensive');

  const activeLens = researchData?.lenses?.[activeTab];
  const compositeGrade = researchData?.composite_grade;
  const filmData = researchData?.film_data;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.94)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '16px',
    }}>
      <div
        className="animate-fadeIn"
        style={{
          background: C.s1,
          border: `1px solid ${C.border}`,
          borderRadius: 20,
          width: '100%',
          maxWidth: 780,
          maxHeight: '92vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* ── Modal Header ─────────────────────────────────────────── */}
        <div style={{
          padding: '20px 24px 0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {/* Composite grade circle */}
            {researchData && compositeGrade != null && (
              <DeepResearchGradeCircle grade={compositeGrade} color={C.gold} size={64} />
            )}
            <div>
              <div style={{ fontSize: 9, color: C.t3, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 4 }}>
                8-LENS DEEP RESEARCH
              </div>
              <div style={{ fontSize: 17, color: C.text, fontWeight: 300 }}>
                {player.playerName}
                <span style={{ color: C.t3, fontSize: 12, marginLeft: 8 }}>
                  {player.position} &bull; {player.school}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                {filmData && (
                  <span style={{
                    fontSize: 9, fontWeight: 600, letterSpacing: 2,
                    padding: '2px 8px', borderRadius: 4,
                    background: C.goldDim, color: C.gold,
                    textTransform: 'uppercase',
                  }}>
                    FILM DATA
                  </span>
                )}
                {researchData && (
                  <span style={{ fontSize: 10, color: C.t3 }}>
                    {researchData.total_sources} sources &bull; {new Date(researchData.researched_at).toLocaleTimeString()}
                  </span>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: C.t2, padding: 4 }}
          >
            <X size={18} />
          </button>
        </div>

        {/* ── Loading State ────────────────────────────────────────── */}
        {researchLoading && !researchData && (
          <div style={{
            padding: '48px 24px',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            gap: 16, flex: 1,
          }}>
            <Loader2 size={32} style={{ color: C.gold, animation: 'spin 1s linear infinite' }} />
            <div style={{ fontSize: 13, color: C.t2, textAlign: 'center' }}>
              Running 8-lens deep research...
            </div>
            <div style={{ fontSize: 11, color: C.t3, textAlign: 'center', maxWidth: 320 }}>
              Searching Exa across 8 analysis lenses and synthesizing with Gemini. This takes 30-60 seconds.
            </div>
            {/* Progress indicators */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center', marginTop: 8 }}>
              {LENS_TABS.map(tab => {
                const TabIcon = tab.icon;
                return (
                  <span key={tab.id} style={{
                    fontSize: 9, fontWeight: 600, letterSpacing: 1,
                    padding: '3px 8px', borderRadius: 4,
                    background: `${tab.color}15`, border: `1px solid ${tab.color}33`, color: tab.color,
                  }}>
                    <TabIcon size={9} style={{ display: 'inline', marginRight: 3, verticalAlign: 'middle' }} />
                    {tab.label}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Tab bar (only show when data loaded) ─────────────────── */}
        {researchData && (
          <>
            <div style={{
              display: 'flex', gap: 2, padding: '14px 24px 0', flexShrink: 0,
              overflowX: 'auto',
            }}>
              {LENS_TABS.map(tab => {
                const isActive = activeTab === tab.id;
                const TabIcon = tab.icon;
                const lensData = researchData.lenses?.[tab.id];
                const grade = lensData?.grade;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    style={{
                      padding: '5px 10px',
                      borderRadius: 100,
                      fontSize: 10,
                      fontWeight: 600,
                      cursor: 'pointer',
                      border: 'none',
                      background: isActive ? tab.color : 'transparent',
                      color: isActive ? '#000' : C.t2,
                      transition: 'all 0.15s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                    }}
                  >
                    <TabIcon size={11} />
                    {tab.label}
                    {grade != null && (
                      <span style={{
                        fontSize: 9, fontFamily: 'monospace', fontWeight: 700,
                        marginLeft: 2,
                        color: isActive ? '#000' : tab.color,
                      }}>
                        {grade.toFixed(1)}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* ── Tab Content ──────────────────────────────────────── */}
            <div style={{ overflowY: 'auto', padding: '20px 24px 24px', flex: 1 }}>
              {activeLens ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {/* Lens header with grade circle */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <DeepResearchGradeCircle
                      grade={activeLens.grade}
                      color={LENS_TABS.find(t => t.id === activeTab)?.color || C.gold}
                      size={80}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, color: C.text, fontWeight: 400, marginBottom: 4 }}>
                        {activeLens.display_name}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <ConfidenceBadge level={activeLens.confidence} />
                        <span style={{ fontSize: 10, color: C.t3 }}>
                          {activeLens.source_count} source{activeLens.source_count !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Summary / AI analysis */}
                  {activeLens.summary && (
                    <div style={{
                      background: C.s2, border: `1px solid ${C.border}`,
                      borderRadius: 10, padding: '14px 16px',
                    }}>
                      <div
                        style={{ fontSize: 12, color: C.t2, lineHeight: 1.7 }}
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(marked.parse(activeLens.summary)),
                        }}
                      />
                    </div>
                  )}

                  {/* Key Findings */}
                  {activeLens.key_findings && activeLens.key_findings.length > 0 && (
                    <div>
                      <div style={{ fontSize: 9, color: C.t3, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>
                        Key Findings
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {activeLens.key_findings.map((finding, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                            <div style={{
                              width: 4, height: 4, borderRadius: '50%',
                              background: LENS_TABS.find(t => t.id === activeTab)?.color || C.gold,
                              marginTop: 6, flexShrink: 0,
                            }} />
                            <span style={{ fontSize: 12, color: C.text, lineHeight: 1.5 }}>{finding}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Strengths + Concerns row */}
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    {/* Strengths */}
                    {activeLens.strengths && activeLens.strengths.length > 0 && (
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={{ fontSize: 9, color: '#00CC66', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>
                          Strengths
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {activeLens.strengths.map((s, i) => (
                            <span key={i} style={{
                              background: '#00CC6612', border: '1px solid #00CC6633',
                              borderRadius: 6, padding: '4px 10px', fontSize: 11, color: '#00CC66',
                            }}>
                              <CheckCircle size={10} style={{ display: 'inline', marginRight: 4, verticalAlign: 'middle' }} />
                              {s}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {/* Concerns */}
                    {activeLens.concerns && activeLens.concerns.length > 0 && (
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={{ fontSize: 9, color: '#FF4444', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>
                          Concerns
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {activeLens.concerns.map((c, i) => (
                            <span key={i} style={{
                              background: '#FF444412', border: '1px solid #FF444433',
                              borderRadius: 6, padding: '4px 10px', fontSize: 11, color: '#FF4444',
                            }}>
                              <AlertCircle size={10} style={{ display: 'inline', marginRight: 4, verticalAlign: 'middle' }} />
                              {c}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Sources */}
                  {researchData.sources && researchData.sources.filter(s => s.lens === activeTab).length > 0 && (
                    <div>
                      <div style={{ fontSize: 9, color: C.t3, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>
                        Sources
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {researchData.sources
                          .filter(s => s.lens === activeTab)
                          .map((source, i) => (
                            <a
                              key={i}
                              href={source.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                display: 'flex', alignItems: 'center', gap: 6,
                                fontSize: 11, color: C.t2, textDecoration: 'none',
                                padding: '4px 8px', borderRadius: 6,
                                background: C.s2, border: `1px solid ${C.border}`,
                                transition: 'border-color 0.15s',
                              }}
                              onMouseEnter={e => { e.currentTarget.style.borderColor = LENS_TABS.find(t => t.id === activeTab)?.color || C.gold; }}
                              onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; }}
                            >
                              <ExternalLink size={10} style={{ flexShrink: 0, color: C.t3 }} />
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {source.title || source.url}
                              </span>
                            </a>
                          ))
                        }
                      </div>
                    </div>
                  )}

                  {/* Film data overlay (if film grades exist for this lens) */}
                  {filmData?.grades_by_lens?.[activeTab] && (
                    <div style={{
                      background: C.goldDim, border: `1px solid ${C.gold}33`,
                      borderRadius: 10, padding: '12px 16px',
                    }}>
                      <div style={{ fontSize: 9, color: C.gold, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 6 }}>
                        Film Data
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ fontSize: 20, fontWeight: 200, color: C.gold, fontFamily: 'monospace' }}>
                          {filmData.grades_by_lens[activeTab].avg_grade?.toFixed(1) || '--'}
                        </span>
                        <span style={{ fontSize: 11, color: C.t2 }}>
                          from {filmData.grades_by_lens[activeTab].mentions} film mention{filmData.grades_by_lens[activeTab].mentions !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ padding: 24, textAlign: 'center', color: C.t3, fontSize: 12 }}>
                  No data available for this lens.
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export default function TransferPortal({ embedded = false }) {
  const { selectedTeam: contextTeam } = useTeam();
  const contextTeamName = contextTeam?.teamName || contextTeam?.team?.team_name || contextTeam?.team?.full_name || 'Missouri';
  const navigate = useNavigate();

  const { isMobile } = useMobileDetect();

  // Filter state
  const [filters, setFilters] = useState({
    position: 'ALL',
    classYear: 'ALL',
    portalStatus: 'ALL',
    search: '',
    sort: 'dxGrade',
  });

  // Data state
  const [players, setPlayers] = useState([]);
  const [playersLoading, setPlayersLoading] = useState(true);
  const [totalPlayers, setTotalPlayers] = useState(0);

  // Selection state
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  // Fit score state
  const [fitScoreCache, setFitScoreCache] = useState({});
  const [currentFitScore, setCurrentFitScore] = useState(null);
  const [fitScoreLoading, setFitScoreLoading] = useState(false);
  const [fitScoreError, setFitScoreError] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Animated score display
  const [displayScore, setDisplayScore] = useState(0);
  const intervalRef = useRef(null);

  // Deep Research state
  const [showDeepResearch, setShowDeepResearch] = useState(false);
  const [deepResearchLoading, setDeepResearchLoading] = useState(false);
  const [deepResearchData, setDeepResearchData] = useState(null);
  const [deepResearchCache, setDeepResearchCache] = useState({});

  // ─── Fetch players (re-fires when filters change) ──────────────────────
  useEffect(() => {
    let cancelled = false;
    setPlayersLoading(true);

    const params = new URLSearchParams();
    if (filters.position !== 'ALL') params.set('position', filters.position);
    if (filters.classYear !== 'ALL') params.set('classYear', filters.classYear);
    if (filters.portalStatus !== 'ALL') params.set('portalStatus', filters.portalStatus);
    if (filters.search) params.set('search', filters.search);
    params.set('sort', filters.sort);
    params.set('limit', '50');

    // IMPORTANT: respond() wraps in json.data.* — must read json.data.players
    fetch(`${API_BASE}/api/basketball/portal/players?${params.toString()}`)
      .then(r => r.json())
      .then(json => {
        if (cancelled) return;
        if (json.success) {
          setPlayers(json.data.players || []);
          setTotalPlayers(json.data.total || 0);
        }
      })
      .catch(err => {
        if (!cancelled) console.error('[TransferPortal] players fetch error:', err);
      })
      .finally(() => {
        if (!cancelled) setPlayersLoading(false);
      });

    return () => { cancelled = true; };
  }, [JSON.stringify(filters)]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Score count-up animation ─────────────────────────────────────────
  useEffect(() => {
    if (!currentFitScore?.overallFitScore) return;
    const target = currentFitScore.overallFitScore;
    if (intervalRef.current) clearInterval(intervalRef.current);
    let current = 0;
    const step = Math.ceil(target / 40);
    intervalRef.current = setInterval(() => {
      current = Math.min(current + step, target);
      setDisplayScore(current);
      if (current >= target) clearInterval(intervalRef.current);
    }, 20);
    return () => clearInterval(intervalRef.current);
  }, [currentFitScore?.overallFitScore]);

  // ─── Select a player ──────────────────────────────────────────────────
  const handleSelectPlayer = useCallback((player) => {
    setSelectedPlayer(player);
    setShowDetail(true);
    setFitScoreError(null);
    // Restore cached fit score if available
    const cached = fitScoreCache[player.playerName];
    if (cached) {
      setCurrentFitScore(cached);
    } else {
      setCurrentFitScore(null);
      setDisplayScore(0);
    }
  }, [fitScoreCache]);

  // ─── Run Fit Score ─────────────────────────────────────────────────────
  const handleRunFitScore = useCallback(async () => {
    if (!selectedPlayer) return;
    setFitScoreLoading(true);
    setCurrentFitScore(null);
    setDisplayScore(0);
    setFitScoreError(null);

    try {
      // IMPORTANT: respond() wraps in json.data.* — must read json.data.fitScore
      const res = await fetch(`${API_BASE}/api/basketball/portal/fit-score`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: getAuthToken() },
        body: JSON.stringify({
          playerName: selectedPlayer.playerName,
          teamContext: {
            team: contextTeamName || 'Missouri',
            league: 'MBB',
            positionNeeds: [],
            playStyle: 'Pick-and-roll heavy, pace-and-space',
          },
        }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message || 'API error');

      const fitScore = json.data.fitScore;
      setFitScoreCache(prev => ({ ...prev, [selectedPlayer.playerName]: fitScore }));
      setCurrentFitScore(fitScore);
    } catch (err) {
      console.error('[TransferPortal] fit score error:', err.message);
      setFitScoreError('Analysis unavailable — try again in 1 minute');
    } finally {
      setFitScoreLoading(false);
    }
  }, [selectedPlayer, contextTeamName]);

  const handleUpdateFilter = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  // ─── Deep Research trigger ─────────────────────────────────────────
  const triggerDeepResearch = useCallback(async (playerName) => {
    // Check cache first
    if (deepResearchCache[playerName]) {
      setDeepResearchData(deepResearchCache[playerName]);
      setShowDeepResearch(true);
      return;
    }

    setDeepResearchLoading(true);
    setDeepResearchData(null);
    setShowDeepResearch(true);

    try {
      const resp = await fetch(`${API_BASE}/api/agent/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: getAuthToken(),
        },
        body: JSON.stringify({
          message: `Use the deepResearchPlayer tool to research "${playerName}" across all 8 analysis lenses. Return the complete structured report.`,
          properties: { sport: 'basketball', playerContext: playerName },
        }),
      });

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let fullResponse = '';
      let foundData = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.type === 'text' && data.content) {
                fullResponse += data.content;
              }
              if (data.type === 'tool_result' && data.toolResult?.data) {
                foundData = data.toolResult.data;
                setDeepResearchData(foundData);
              }
            } catch { /* skip unparseable SSE chunks */ }
          }
        }
      }

      // If no direct tool_result, try parsing the accumulated text response
      if (!foundData && fullResponse) {
        try {
          const jsonMatch = fullResponse.match(/\{[\s\S]*"lenses"[\s\S]*\}/);
          if (jsonMatch) {
            const parsed = JSON.parse(jsonMatch[0]);
            if (parsed.lenses) {
              foundData = parsed;
              setDeepResearchData(foundData);
            }
          }
        } catch { /* response was narrative, not structured */ }
      }

      // Cache the result
      if (foundData) {
        setDeepResearchCache(prev => ({ ...prev, [playerName]: foundData }));
      }
    } catch (err) {
      console.error('[TransferPortal] Deep research failed:', err);
    } finally {
      setDeepResearchLoading(false);
    }
  }, [deepResearchCache]);

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════

  const cachedScoreForSelected = selectedPlayer ? fitScoreCache[selectedPlayer.playerName] : null;

  return (
    <>
      {/* Inject keyframes for modal fade-in */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.25s ease forwards; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      <div style={embedded ? { color: C.text } : { background: C.bg, minHeight: '100vh', color: C.text }}>
        <div style={embedded ? { paddingTop: 0 } : { padding: '20px 24px' }}>

          {/* ── PAGE HEADER ─────────────────────────────────────────────── */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{
                background: C.goldDim, border: `1px solid ${C.gold}44`,
                borderRadius: 100, padding: '4px 12px',
                fontSize: 10, fontWeight: 600, letterSpacing: 2,
                color: C.gold, textTransform: 'uppercase',
              }}>
                Transfer Portal
              </span>
              <span style={{ fontSize: 11, color: C.t3 }}>
                DraftExpress Gold &bull; {totalPlayers} prospects
              </span>
            </div>
            <div style={{ fontSize: 11, color: C.t2 }}>
              Program: <span style={{ color: C.gold }}>{contextTeamName || 'Select team'}</span>
            </div>
          </div>

          {/* ── MAIN LAYOUT ─────────────────────────────────────────────── */}
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 16,
          }}>
            {/* ── LEFT PANEL — Player List ─────────────────────────────── */}
            {(!isMobile || !showDetail) && (
              <div style={{
                width: isMobile ? '100%' : '38%',
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
              }}>
                {/* Filters */}
                <div style={{
                  background: C.s1, border: `1px solid ${C.border}`,
                  borderRadius: 14, padding: '14px 16px',
                  display: 'flex', flexDirection: 'column', gap: 10,
                }}>
                  {/* Search */}
                  <input
                    type="text"
                    placeholder="Search by name or school..."
                    value={filters.search}
                    onChange={e => handleUpdateFilter('search', e.target.value)}
                    style={{
                      background: C.s2, border: `1px solid ${C.border}`,
                      borderRadius: 8, padding: '8px 12px',
                      fontSize: 12, color: C.text, outline: 'none', width: '100%',
                    }}
                  />
                  {/* Position filter */}
                  <div>
                    <div style={{ fontSize: 9, color: C.t3, letterSpacing: 2, marginBottom: 6, textTransform: 'uppercase' }}>Position</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {POSITIONS.map(p => (
                        <FilterPill key={p} label={p} active={filters.position === p} onClick={() => handleUpdateFilter('position', p)} />
                      ))}
                    </div>
                  </div>
                  {/* Class filter */}
                  <div>
                    <div style={{ fontSize: 9, color: C.t3, letterSpacing: 2, marginBottom: 6, textTransform: 'uppercase' }}>Class</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {CLASS_YEARS.map(c => (
                        <FilterPill key={c} label={c === 'ALL' ? 'ALL' : c.slice(0, 2).toUpperCase()} active={filters.classYear === c} onClick={() => handleUpdateFilter('classYear', c)} />
                      ))}
                    </div>
                  </div>
                  {/* Status filter */}
                  <div>
                    <div style={{ fontSize: 9, color: C.t3, letterSpacing: 2, marginBottom: 6, textTransform: 'uppercase' }}>Status</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {PORTAL_STATUSES.map(s => (
                        <FilterPill key={s} label={s} active={filters.portalStatus === s} onClick={() => handleUpdateFilter('portalStatus', s)} />
                      ))}
                    </div>
                  </div>
                  {/* Sort */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 9, color: C.t3, letterSpacing: 2, textTransform: 'uppercase' }}>Sort</span>
                    <select
                      value={filters.sort}
                      onChange={e => handleUpdateFilter('sort', e.target.value)}
                      style={{
                        background: C.s2, border: `1px solid ${C.border}`,
                        borderRadius: 6, padding: '4px 8px',
                        fontSize: 11, color: C.text, cursor: 'pointer',
                      }}
                    >
                      {SORT_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Player list */}
                <div style={{
                  background: C.s1, border: `1px solid ${C.border}`,
                  borderRadius: 14, overflow: 'hidden',
                }}>
                  {playersLoading ? (
                    <div style={{ padding: 24, textAlign: 'center', color: C.t3, fontSize: 12 }}>
                      <Loader2 size={16} className="inline mr-2" style={{ display: 'inline', marginRight: 6, color: C.gold }} />
                      Loading prospects...
                    </div>
                  ) : players.length === 0 ? (
                    <div style={{ padding: 24, textAlign: 'center', color: C.t3, fontSize: 12 }}>
                      No prospects found matching filters.
                    </div>
                  ) : (
                    <div style={{ maxHeight: 540, overflowY: 'auto' }}>
                      {players.map((player, idx) => {
                        const isSelected = selectedPlayer?.playerName === player.playerName;
                        const cachedScore = fitScoreCache[player.playerName];
                        return (
                          <div
                            key={player.playerName + idx}
                            onClick={() => handleSelectPlayer(player)}
                            style={{
                              padding: '12px 16px',
                              cursor: 'pointer',
                              background: isSelected ? C.goldDim : 'transparent',
                              borderBottom: `1px solid ${C.border}`,
                              borderLeft: isSelected ? `2px solid ${C.gold}` : '2px solid transparent',
                              transition: 'background 0.15s',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              gap: 8,
                            }}
                          >
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, color: C.text, fontWeight: 400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {player.playerName}
                              </div>
                              <div style={{ fontSize: 10, color: C.t2, marginTop: 2 }}>
                                {player.position} &bull; {player.school}
                              </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                              <span style={{ fontSize: 11, fontFamily: 'monospace', color: C.gold, fontWeight: 600 }}>
                                {player.dxGrade}
                              </span>
                              {cachedScore && <ScoreBadge score={cachedScore.overallFitScore} small />}
                              {isMobile && isSelected && <ChevronRight size={12} style={{ color: C.t3 }} />}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── RIGHT PANEL — Detail ─────────────────────────────────── */}
            {(!isMobile || showDetail) && (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* Mobile back button */}
                {isMobile && showDetail && (
                  <button
                    onClick={() => { setShowDetail(false); setSelectedPlayer(null); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      background: 'transparent', border: 'none',
                      cursor: 'pointer', color: C.t2, fontSize: 12, padding: 0,
                    }}
                  >
                    <ArrowLeft size={14} /> Back to list
                  </button>
                )}

                {!selectedPlayer ? (
                  /* Empty state */
                  <div style={{
                    background: C.s1, border: `1px solid ${C.border}`,
                    borderRadius: 14, padding: 48,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    minHeight: 320,
                  }}>
                    <BarChart3 size={28} style={{ color: C.t3, marginBottom: 12 }} />
                    <p style={{ fontSize: 13, color: C.t2, textAlign: 'center', maxWidth: 280, lineHeight: 1.6 }}>
                      Select a prospect to run SLAM Fit Score analysis
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Player header card */}
                    <div style={{
                      background: C.s1, border: `1px solid ${C.border}`,
                      borderRadius: 14, padding: '16px 20px',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                            <span style={{ fontSize: 26, fontWeight: 200, color: C.text, letterSpacing: -1 }}>
                              {selectedPlayer.playerName}
                            </span>
                            <button
                              onClick={() => navigate(`/basketball/gm/player/${encodeURIComponent(selectedPlayer.playerName)}`)}
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                background: 'transparent', border: `1px solid ${C.border}`,
                                borderRadius: 100, padding: '3px 10px', cursor: 'pointer',
                                fontSize: 10, color: C.t2, transition: 'all 0.15s',
                              }}
                              onMouseEnter={e => { e.currentTarget.style.borderColor = C.gold; e.currentTarget.style.color = C.gold; }}
                              onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.t2; }}
                            >
                              <ExternalLink size={10} /> Full Profile
                            </button>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{
                              background: C.goldDim, border: `1px solid ${C.gold}44`,
                              borderRadius: 100, padding: '3px 10px',
                              fontSize: 10, color: C.gold, fontWeight: 600, letterSpacing: 1,
                            }}>
                              {selectedPlayer.position}
                            </span>
                            <span style={{ fontSize: 12, color: C.t2 }}>{selectedPlayer.school}</span>
                            <span style={{ fontSize: 12, color: C.t3 }}>{selectedPlayer.classYear}</span>
                            {selectedPlayer.height && (
                              <span style={{ fontSize: 12, color: C.t3 }}>{selectedPlayer.height}</span>
                            )}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <span style={{
                            fontSize: 9, fontWeight: 600, letterSpacing: 2,
                            padding: '3px 8px', borderRadius: 6,
                            background: selectedPlayer.portalStatus === 'Available'
                              ? `${C.elite}20` : `${C.gold}20`,
                            color: selectedPlayer.portalStatus === 'Available' ? C.elite : C.gold,
                            border: `1px solid ${selectedPlayer.portalStatus === 'Available' ? C.elite : C.gold}44`,
                            textTransform: 'uppercase',
                          }}>
                            {selectedPlayer.portalStatus}
                          </span>
                          {selectedPlayer.dxGrade > 0 && (
                            <div style={{ marginTop: 6, fontSize: 11, color: C.t3 }}>
                              DX Grade: <span style={{ color: C.gold, fontFamily: 'monospace', fontWeight: 600 }}>
                                {selectedPlayer.dxGrade}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Stats grid */}
                    <div style={{
                      background: C.s1, border: `1px solid ${C.border}`,
                      borderRadius: 14, padding: '16px 20px',
                    }}>
                      <div style={{ fontSize: 9, color: C.t3, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 12 }}>
                        Season Stats
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 6 }}>
                        <StatCell label="PPG" value={selectedPlayer.stats.ppg || '--'} />
                        <StatCell label="RPG" value={selectedPlayer.stats.rpg || '--'} />
                        <StatCell label="APG" value={selectedPlayer.stats.apg || '--'} />
                        <StatCell label="FG%" value={selectedPlayer.stats.fgPct ? `${selectedPlayer.stats.fgPct}%` : '--'} />
                        <StatCell label="3P%" value={selectedPlayer.stats.fg3Pct ? `${selectedPlayer.stats.fg3Pct}%` : '--'} />
                        <StatCell label="FT%" value={selectedPlayer.stats.ftPct ? `${selectedPlayer.stats.ftPct}%` : '--'} />
                        <StatCell label="PER" value={selectedPlayer.stats.per || '--'} />
                        <StatCell label="P40" value={selectedPlayer.stats.p40 || '--'} />
                        <StatCell label="R40" value={selectedPlayer.stats.r40 || '--'} />
                        <StatCell label="A40" value={selectedPlayer.stats.a40 || '--'} />
                        <StatCell label="DXV" value={selectedPlayer.dxGrade || '--'} />
                        <StatCell label="GP" value={selectedPlayer.stats.gamesPlayed || '--'} />
                      </div>
                    </div>

                    {/* SLAM Fit Score card */}
                    <div style={{
                      background: C.s1, border: `1px solid ${C.border}`,
                      borderRadius: 14, padding: '20px',
                    }}>
                      <div style={{ fontSize: 9, color: C.t3, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 16 }}>
                        SLAM Fit Score
                      </div>

                      {/* Score + tier */}
                      {currentFitScore && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
                          <div style={{
                            fontSize: 56, fontWeight: 200, color: portalTier(currentFitScore.overallFitScore).color,
                            fontFamily: 'monospace', letterSpacing: -3, lineHeight: 1,
                          }}>
                            {displayScore}
                          </div>
                          <div>
                            <div style={{
                              fontSize: 11, fontWeight: 600,
                              color: portalTier(currentFitScore.overallFitScore).color,
                              marginBottom: 4,
                            }}>
                              {currentFitScore.fitTier}
                            </div>
                            <div style={{ fontSize: 10, color: C.t3 }}>
                              Confidence: {currentFitScore.confidence}%
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Dimension rows */}
                      {currentFitScore && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
                          {DIMENSION_CONFIG.map(dim => {
                            const score = currentFitScore[dim.key] || 0;
                            const isChar = dim.key === 'characterScore';
                            const isCog = dim.key === 'cognitiveScore';
                            const DimIcon = dim.icon;
                            return (
                              <div key={dim.key}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                  <DimIcon size={11} style={{ color: dim.color }} />
                                  <span style={{ fontSize: 10, color: C.t2, flex: 1 }}>{dim.label}</span>
                                  {isChar && <ScoutingNotesBadge />}
                                  {isCog && <DataSourceBadge source={currentFitScore.cognitiveDataSource} />}
                                  <span style={{ fontSize: 12, fontFamily: 'monospace', color: dim.color, fontWeight: 600 }}>
                                    {score}
                                  </span>
                                </div>
                                <ProgressBar value={score} color={dim.color} />
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* NIL estimate in summary */}
                      {currentFitScore && currentFitScore.nilEstimate > 0 && (
                        <div style={{ marginBottom: 16, fontSize: 12, color: C.t2 }}>
                          NIL Est.{' '}
                          <span style={{ color: C.gold, fontFamily: 'monospace' }}>
                            ~${(currentFitScore.nilEstimate / 1000).toFixed(0)}K/yr
                          </span>
                          <span style={{ fontSize: 10, color: C.t3, marginLeft: 6 }}>
                            AI estimate — not a verified marketplace figure
                          </span>
                        </div>
                      )}

                      {/* Action buttons */}
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        {!currentFitScore && !fitScoreLoading && (
                          <button
                            onClick={handleRunFitScore}
                            style={{
                              padding: '10px 24px', borderRadius: 100,
                              background: C.goldDim, border: `1px solid ${C.gold}44`,
                              color: C.gold, fontSize: 12, fontWeight: 500,
                              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                            }}
                          >
                            <Zap size={13} /> Run Fit Score
                          </button>
                        )}
                        {fitScoreLoading && (
                          <button disabled style={{
                            padding: '10px 24px', borderRadius: 100,
                            background: C.s3, border: `1px solid ${C.border}`,
                            color: C.t2, fontSize: 12, cursor: 'not-allowed',
                            display: 'flex', alignItems: 'center', gap: 6,
                          }}>
                            <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />
                            Analyzing...
                          </button>
                        )}
                        {currentFitScore && !fitScoreLoading && (
                          <>
                            <button
                              onClick={() => setShowModal(true)}
                              style={{
                                padding: '10px 24px', borderRadius: 100,
                                background: C.goldDim, border: `1px solid ${C.gold}44`,
                                color: C.gold, fontSize: 12, fontWeight: 500,
                                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                              }}
                            >
                              View Full Breakdown <ChevronRight size={13} />
                            </button>
                            <button
                              onClick={handleRunFitScore}
                              style={{
                                padding: '10px 20px', borderRadius: 100,
                                background: 'transparent', border: `1px solid ${C.border}`,
                                color: C.t2, fontSize: 11, cursor: 'pointer',
                              }}
                            >
                              Re-run
                            </button>
                          </>
                        )}
                      </div>

                      {/* Error state */}
                      {fitScoreError && (
                        <p style={{ fontSize: 11, color: C.dev, marginTop: 8 }}>
                          {fitScoreError}
                        </p>
                      )}
                    </div>

                    {/* Key recruiting insight (if fit score loaded) */}
                    {currentFitScore && currentFitScore.keyRecruitingInsight && (
                      <div style={{
                        background: C.s1,
                        border: `1px solid ${C.border}`,
                        borderLeft: `3px solid ${C.gold}`,
                        borderRadius: '0 10px 10px 0',
                        padding: '12px 16px',
                      }}>
                        <div style={{ fontSize: 9, color: C.gold, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 6 }}>
                          Key Recruiting Insight
                        </div>
                        <p style={{ fontSize: 12, color: C.text, lineHeight: 1.7, margin: 0 }}>
                          {currentFitScore.keyRecruitingInsight}
                        </p>
                      </div>
                    )}

                    {/* 8-Lens Deep Research CTA */}
                    <button
                      onClick={() => triggerDeepResearch(selectedPlayer.playerName)}
                      disabled={deepResearchLoading}
                      style={{
                        padding: '10px 20px', borderRadius: 10,
                        background: C.s1, border: `1px solid #FF6B0044`,
                        color: '#FF6B00', fontSize: 12, fontWeight: 500,
                        cursor: deepResearchLoading ? 'not-allowed' : 'pointer',
                        display: 'flex', alignItems: 'center', gap: 8,
                        width: '100%', justifyContent: 'center',
                        transition: 'border-color 0.15s',
                        opacity: deepResearchLoading ? 0.6 : 1,
                      }}
                    >
                      {deepResearchLoading ? (
                        <>
                          <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />
                          Deep Research Running...
                        </>
                      ) : (
                        <>
                          <Search size={13} />
                          8-Lens Deep Research
                          {deepResearchCache[selectedPlayer.playerName] && (
                            <span style={{
                              fontSize: 9, fontWeight: 700,
                              padding: '1px 6px', borderRadius: 4,
                              background: '#00CC6618', color: '#00CC66',
                              border: '1px solid #00CC6633',
                            }}>
                              CACHED
                            </span>
                          )}
                          <ChevronRight size={12} />
                        </>
                      )}
                    </button>

                    {/* Ask SLAM AI CTA */}
                    <button
                      onClick={() => window.open(
                        `/basketball/ask-slam-ai?q=${encodeURIComponent(`Tell me about ${selectedPlayer.playerName} from ${selectedPlayer.school}`)}`,
                        '_blank'
                      )}
                      style={{
                        padding: '10px 20px', borderRadius: 10,
                        background: C.s1, border: `1px solid ${C.border}`,
                        color: C.t2, fontSize: 12, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 8,
                        width: '100%', justifyContent: 'center',
                        transition: 'border-color 0.15s',
                      }}
                    >
                      <Brain size={13} style={{ color: C.gold }} />
                      Ask SLAM AI about {selectedPlayer.playerName}
                      <ChevronRight size={12} />
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Full Breakdown Modal */}
      {showModal && selectedPlayer && cachedScoreForSelected && (
        <FitScoreBreakdownModal
          player={selectedPlayer}
          fitScore={cachedScoreForSelected}
          onClose={() => setShowModal(false)}
        />
      )}

      {/* Deep Research Modal */}
      {showDeepResearch && selectedPlayer && (
        <DeepResearchModal
          player={selectedPlayer}
          researchData={deepResearchData}
          researchLoading={deepResearchLoading}
          onClose={() => setShowDeepResearch(false)}
        />
      )}
    </>
  );
}
